import { useEffect, useId, useRef, useState } from 'react'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CloseIcon from '@mui/icons-material/Close'
import ModeCommentRoundedIcon from '@mui/icons-material/ModeCommentRounded';
import CommentForm from './CommentForm'
import { Heart } from '../bits'
import { formatMessageTime, readImage } from '../logic'

export default function TextCard({ text, image = null, allowImage = false, canEdit, onSave, onDelete, meta, at, compact = false, mine = false, like = null, canComment = false, onCommenting }) {
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState(text)
    const [draftImage, setDraftImage] = useState(image)
    const [confirming, setConfirming] = useState(false)
    const [commenting, setCommenting] = useState(false)
    const [showLikes, setShowLikes] = useState(false)
    const [zoom, setZoom] = useState(false)
    const deleteTitleId = useId()
    const likeRef = useRef(null)
    const fileRef = useRef(null)
    useEffect(() => {
      if (!confirming) return undefined
      const onKey = (event) => {
        if (event.key === 'Escape') setConfirming(false)
      }
      document.addEventListener('keydown', onKey)
      return () => document.removeEventListener('keydown', onKey)
    }, [confirming])
    useEffect(() => {
      if (!showLikes) return undefined
      const onPointer = (event) => {
        if (!likeRef.current?.contains(event.target)) setShowLikes(false)
      }
      const onKey = (event) => {
        if (event.key === 'Escape') setShowLikes(false)
      }
      document.addEventListener('pointerdown', onPointer)
      document.addEventListener('keydown', onKey)
      return () => {
        document.removeEventListener('pointerdown', onPointer)
        document.removeEventListener('keydown', onKey)
      }
    }, [showLikes])
    useEffect(() => {
      if (!zoom) return undefined
      const onKey = (event) => {
        if (event.key === 'Escape') setZoom(false)
      }
      document.addEventListener('keydown', onKey)
      return () => document.removeEventListener('keydown', onKey)
    }, [zoom])
    const changed = draft !== text || (draftImage || null) !== (image || null)
    const canSave = changed && Boolean(draft.trim() || draftImage)
  
    return (
      <>
      <article className={`hold-card hold-bubble${compact ? ' hold-bubble--comment' : ''}${mine ? ' hold-bubble--mine' : ''}`}>
        <p className="hold-meta hold-bubble-meta">
          <span>{meta}</span>
          {at ? <time dateTime={at}>{formatMessageTime(at)}</time> : null}
        </p>
        {editing ? (
          <form
            onSubmit={(event) => {
              event.preventDefault()
              if (!draft.trim() && !draftImage) return
              onSave(draft, draftImage)
              setEditing(false)
            }}
          >
            <textarea value={draft} onChange={(event) => setDraft(event.target.value)} rows={3} />
            {draftImage ? (
              <div className="hold-photo-draft">
                <img src={draftImage} alt="" />
                <button type="button" className="hold-text-button" onClick={() => setDraftImage(null)}>Fjern billede</button>
              </div>
            ) : null}
            <div className="hold-row">
              {allowImage ? (
                <>
                  <button type="button" className="hold-text-button" onClick={() => fileRef.current?.click()}>Skift billede</button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={async (event) => {
                      const file = event.target.files?.[0]
                      event.target.value = ''
                      if (!file) return
                      try {
                        setDraftImage(await readImage(file))
                      } catch {
                        setDraftImage(draftImage)
                      }
                    }}
                  />
                </>
              ) : null}
              <button type="submit" className="hold-action hold-action--small hold-action--save" disabled={!canSave}>Gem</button>
              <button type="button" className="hold-text-button" onClick={() => { setDraft(text); setDraftImage(image); setEditing(false) }}>
                Annuller
              </button>
            </div>
          </form>
        ) : (
          <>
            {image ? (
              <button type="button" className="hold-photo-open" aria-label="Vis billedet stort" onClick={() => setZoom(true)}>
                <img className="hold-photo" src={image} alt="" />
              </button>
            ) : null}
            {text ? <p className="hold-copy">{text}</p> : null}
          </>
        )}
        {!editing && (like || canEdit) ? (
          <div className="hold-card-tools">
            {like ? (
              <div className="hold-like-wrap" ref={likeRef}>
                <button
                  type="button"
                  className={`hold-like${like.on ? ' is-on' : ''}`}
                  aria-pressed={Boolean(like.on)}
                  disabled={!like.onToggle}
                  aria-label={like.onToggle ? (like.on ? 'Fjern synes godt om' : 'Synes godt om') : 'Dit opslag'}
                  onClick={like.onToggle || undefined}
                >
                  <Heart filled={Boolean(like.on) || (!like.onToggle && like.count > 0)} />
                  {like.count === 0 && like.onToggle ? <span>Synes godt om</span> : null}
                </button>
                {like.count > 0 ? (
                  <button
                    type="button"
                    className={`hold-like-count${like.on ? ' is-on' : ''}`}
                    aria-expanded={showLikes}
                    onClick={() => setShowLikes((open) => !open)}
                  >
                    {like.count}
                  </button>
                ) : null}
                {showLikes && like.people?.length ? (
                  <ul className="hold-like-list">
                    {like.people.map((person) => <li key={person.id}>{person.name}</li>)}
                  </ul>
                ) : null}
              </div>
            ) : <span />}
            <div className="hold-card-tools-edit">
            {canComment ? (
                <button type="button" className="hold-icon-button" aria-label="Tilføj kommentar" onClick={() => setCommenting(!commenting)}>
                    <ModeCommentRoundedIcon />
                </button>
            ) : null}
            {canEdit ? (
              <>
                <button type="button" className="hold-icon-button" aria-label="Ret" onClick={() => setEditing(true)}>
                  <EditOutlinedIcon />
                </button>
                <button type="button" className="hold-icon-button hold-icon-button--delete" aria-label="Slet" onClick={() => setConfirming(true)}>
                  <DeleteOutlineIcon />
                </button>
              </>
            ) : null}
            </div>
          </div>
        ) : null}
  
        {zoom && image ? (
          <div className="hold-lightbox" role="dialog" aria-modal="true" aria-label="Billede">
            <button type="button" className="hold-lightbox-close" aria-label="Luk" onClick={() => setZoom(false)}>
              <CloseIcon />
            </button>
            <img src={image} alt="" />
          </div>
        ) : null}
        {confirming ? (
          <div className="hold-modal" role="dialog" aria-modal="true" aria-labelledby={deleteTitleId}>
            <button type="button" className="hold-modal-backdrop" aria-label="Luk" onClick={() => setConfirming(false)} />
            <div className="hold-modal-card">
              <h2 id={deleteTitleId}>{compact ? 'Slet kommentar' : 'Slet opslag'}</h2>
              <p className="hold-copy">Det kan ikke fortrydes.</p>
              <div className="hold-modal-actions">
                <button type="button" className="hold-action hold-action--ghost" onClick={() => setConfirming(false)}>Annuller</button>
                <button type="button" className="hold-action hold-action--leave" onClick={onDelete}>Slet</button>
              </div>
            </div>
          </div>
        ) : null}
      </article>
      {commenting ? (
        <CommentForm
          onSubmit={(commentText) => { onCommenting?.(commentText); setCommenting(false) }}
        />
      ) : null}
      </>
    )
  }